import React, { useMemo, useState } from 'react';
import { Card, Button, Table, Space, Popconfirm, Modal, Form, Input, InputNumber } from 'antd';
import { PlusOutlined, DeleteOutlined, ReloadOutlined, EditOutlined } from '@ant-design/icons';
import useCoachCourses from '../hooks/useCoachCourses';
import CourseSelector from '../components/CourseSelector';
import CurriculumList from '../components/CurriculumList';

const AddCourseModal = ({ open, onCancel, onSubmit }) => {
  const [selectedCurriculum, setSelectedCurriculum] = useState(null);
  const [selectedActions, setSelectedActions] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handleOk = async () => {
    if (!selectedCurriculum) {
      return Modal.warning({ title: '请选择一个课程体系', content: '请先在统一定价列表中选择一个课程作为基础。' });
    }

    if (!selectedActions.length) {
      return Modal.warning({ title: '请选择动作', content: '请从动作库中至少选择一个动作。' });
    }

    setSubmitting(true);
    try {
      await onSubmit({
        courseDesc: selectedCurriculum.curriculumName,
        coursePrice: selectedCurriculum.price,
        relateAction: selectedActions.map((action) => action.seq).join(','), // 存储seq数值
        relateActionShow: selectedActions.map((action) => action.eventTitle).join(',') // 展示eventTitle文字
      });
      setSelectedCurriculum(null);
      setSelectedActions([]);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="添加我的课程"
      width={1000}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={submitting}
      okText="提交"
      cancelText="取消"
      destroyOnClose
      maskClosable={false}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card title="统一定价课程" size="small">
          <CurriculumList
            enableSelection
            showHeader={false}
            showSearch
            pageSize={5}
            selectedCurriculumId={selectedCurriculum?.id}
            onCurriculumSelect={(curriculum) => setSelectedCurriculum(curriculum)}
          />
        </Card>
        <Card title="动作库选择" size="small">
          <CourseSelector onCoursesSelect={(actions) => setSelectedActions(actions)} />
        </Card>
      </Space>
    </Modal>
  );
};

const EditCourseModal = ({ open, onCancel, onSubmit, courseData }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await onSubmit({
        ...values,
        id: courseData?.id,
      });
      form.resetFields();
      setSubmitting(false);
      onCancel();
    } catch (error) {
      console.error('表单验证失败:', error);
    }
  };

  // 当课程数据变化时重置表单
  React.useEffect(() => {
    if (courseData && open) {
      form.setFieldsValue({
        courseDesc: courseData.courseDesc,
        coursePrice: courseData.coursePrice,
        relateAction: courseData.relateAction || (courseData.relateActions && Array.isArray(courseData.relateActions) ? courseData.relateActions.map(action => action.seq).join(',') : ''),
        relateActionShow: courseData.relateActionShow || (courseData.relateActions && Array.isArray(courseData.relateActions) ? courseData.relateActions.map(action => action.eventTitle).join(',') : '')
      });
    }
  }, [courseData, open, form]);

  return (
    <Modal
      open={open}
      title="编辑课程"
      width={600}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={submitting}
      okText="保存"
      cancelText="取消"
      destroyOnClose
      maskClosable={false}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          courseDesc: courseData?.courseDesc,
          coursePrice: courseData?.coursePrice,
          relateAction: courseData?.relateAction || (courseData?.relateActions && Array.isArray(courseData.relateActions) ? courseData.relateActions.map(action => action.seq).join(',') : ''),
          relateActionShow: courseData?.relateActionShow || (courseData?.relateActions && Array.isArray(courseData.relateActions) ? courseData.relateActions.map(action => action.eventTitle).join(',') : '')
        }}
      >
        <Form.Item
          label="课程名称"
          name="courseDesc"
          rules={[{ required: true, message: '请输入课程名称' }]}
        >
          <Input placeholder="请输入课程名称" />
        </Form.Item>
        <Form.Item
          label="课程价格"
          name="coursePrice"
          rules={[
            { required: true, message: '请输入课程价格' },
            { type: 'number', min: 0, message: '价格不能为负数' }
          ]}
        >
          <InputNumber
            style={{ width: '100%' }}
            min={0}
            precision={2}
            placeholder="请输入课程价格"
          />
        </Form.Item>
        <Form.Item
          label="关联动作"
          name="relateActionShow"
        >
          <Input.TextArea 
            rows={4} 
            placeholder="关联动作（仅用于展示）" 
            disabled
          />
        </Form.Item>
        {/* 隐藏字段用于存储实际的seq值 */}
        <Form.Item
          name="relateAction"
          hidden
        >
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const MyCourse = () => {
  const { courses, loading, pagination, fetchCourses, addCourse, updateCourse, deleteCourse } = useCoachCourses();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);

  const columns = useMemo(
    () => [
      {
        title: '课程名称',
        dataIndex: 'courseDesc',
        key: 'courseDesc',
      },
      {
        title: '课程定价',
        dataIndex: 'coursePrice',
        key: 'coursePrice',
        render: (value) => `¥${value ?? '-'}`,
      },
      {
        title: '关联动作',
        key: 'relateActions',
        render: (_, record) => {
          // 优先使用relateActionShow，否则从relateActions生成展示内容
          return record.relateActionShow || (record.relateActions && Array.isArray(record.relateActions) ? record.relateActions.map((action) => action.eventTitle).join(', ') : '-') || '-';
        },
      },
      {
        title: '操作',
        key: 'actions',
        width: 200,
        render: (_, record) => (
          <Space size="middle">
            <Button 
              type="link" 
              icon={<EditOutlined />}
              onClick={() => {
                setEditingCourse(record);
                setEditModalOpen(true);
              }}
            >
              编辑
            </Button>
            <Popconfirm
              title="确认删除?"
              description={`确定要删除课程【${record.courseDesc}】吗？`}
              okText="删除"
              cancelText="取消"
              okButtonProps={{ danger: true }}
              onConfirm={() => deleteCourse(record.id)}
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    []
  );

  return (
    <Card
      title="我的课程"
      extra={
        <Space>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={() => fetchCourses(pagination.current, pagination.pageSize)}
            loading={loading}
          >
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
            添加课程
          </Button>
        </Space>
      }
    >
      <Table
        columns={columns}
        rowKey="id"
        loading={loading}
        dataSource={courses}
        pagination={{
          ...pagination,
          onChange: (page, pageSize) => fetchCourses(page, pageSize),
        }}
      />
      <AddCourseModal
        open={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        onSubmit={async (payload) => {
          await addCourse(payload);
          setAddModalOpen(false);
        }}
      />
      <EditCourseModal
        open={editModalOpen}
        onCancel={() => {
          setEditModalOpen(false);
          setEditingCourse(null);
        }}
        onSubmit={async (payload) => {
          await updateCourse(payload);
        }}
        courseData={editingCourse}
      />
    </Card>
  );
};

export default MyCourse;